import {
  Component,
  OnInit,
  ChangeDetectorRef,
  Input,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataService } from '@/app/services/shared/stage2.service';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { NgToastService } from 'ng-angular-popup';
import { NgxPageScrollModule } from 'ngx-page-scroll';
import { MeasurementStandardsComponent } from '@/app/components/projects/project-stages/plan-sections/measurement-standards/measurement-standards.component';
import { DialogboxComponent } from '@/app/components/projects/project-stages/plan-sections/dialogbox/dialogbox.component';
import { MatDialog } from '@angular/material/dialog';
import { GetProjectIdService } from '@/app/services/getProjectId.service';
import {
  PlanStep,
  ProgressService,
} from '@/app/services/shared/progress.service';
import { TabbedScrollerComponent } from '../../tabbed-scroller/tabbed-scroller.component';
import { DialogUtilityService } from '@/app/services/utility/dialog-utility.service';
import { ProgressBarComponent } from '../../progress-bar/progress-bar.component';

/**
 * NOTE:
 *  properties with ? are optional
 *  In TypeScript, interfaces are not strict data structures; they are contracts
 *  that describe the shape of an object. They are used at compile-time for type
 *  checking but do not enforce or validate object shapes at runtime.
 *
 *  The WorkPackageInterface defines the expected structure of an object.
 *  TypeScript checks during development that objects assigned to originalCollection conform to the interface.
 *  However, at runtime, TypeScript has no enforcement mechanism—JavaScript doesn't care about the interface.
 *
 *  When assigning an object to a variable typed as WorkPackageInterface, TypeScript
 *    only checks that the object has the required properties and their types match.
 *  If additional properties are present, they are ignored as long as they don't
 *    contradict the defined structure.
 */
export interface WorkPackageInterface {
  id?: number;
  name?: string;
  parent_id: number;
  parent_name: string;
  suffix?: string;
  children?: WorkPackageInterface[];
}

@Component({
  selector: 'app-workpackages',
  standalone: true,
  imports: [
    MeasurementStandardsComponent,
    MatIconModule,
    FormsModule,
    CommonModule,
    NgxPageScrollModule,
    ProgressBarComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './workpackages.component.html',
  styleUrl: './workpackages.component.css',
})
export class WorkPackagesComponent implements OnInit {
  readonly dialog = inject(MatDialog);
  @Input() projectId: number | null;
  isLoadingNext = false;
  isLoadingSave = false;
  subCategory: WorkPackageInterface | undefined;

  hoveredParentId: number | undefined | null = null;
  hoveredChildId: number | undefined | null = null;
  hoveredSelectedId: number | undefined | null = null;

  WPMainParents: any;
  isManual: any;

  expandedLIds = new Set<number>();
  expandedRIds = new Set<number>();

  @Input() originalCollection: WorkPackageInterface[] = [];
  @Input() collectionL: WorkPackageInterface[] = [];
  @Input() collectionR: WorkPackageInterface[] = [];
  @Input() selectedParentWP: WorkPackageInterface | undefined;
  @Input() isEdit: boolean = false;

  isUpdated: boolean = false;
  selectedSection = 'section1';
  stage2Progress: any;
  constructor(
    private location: Location,
    private dataService: DataService,
    private projectsService: ProjectsService,
    private toast: NgToastService,
    private cdr: ChangeDetectorRef,
    private getProjectIdService: GetProjectIdService,
    private progressService: ProgressService,
    private TabbedScrollerComponent: TabbedScrollerComponent,
    private dialogService: DialogUtilityService
  ) {}

  ngOnInit() {
    this.progressService.progress2$.subscribe((value) => {
      this.stage2Progress = value;
    });
    this.projectId = +this.getProjectIdService.getProjectId();
    this.getAllWPSI();
    this.isManual = localStorage.getItem('isManual');
  }

  /**
   * NOTE: get all work packages and Initialize the left side panel
   */
  getAllWPSI(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.projectsService.getAllWPSI().subscribe({
        next: (data) => {
          this.WPMainParents = data.data;
          this.initializeCollectionL();
          resolve(); // Resolve the promise after data is loaded
        },
        error: (error) => {
          console.error('Error fetching WPSI:', error);
          reject(error); // Reject if there's an error
        },
      });
    });
  }

  /**
   * NOTE: After fetching data, populate left panel with the children of work packages
   *  this.WPMainParents has all children upto three level
   * This function just populates children in left panel
   */
  initializeCollectionL() {
    if (this.isEdit && this.selectedParentWP && !this.isUpdated) {
      return;
    }
    if (!this.WPMainParents) {
      console.warn(`No Work Packages found: ${this.selectedParentWP}`);
      return;
    }

    // Find the Work Package that matches this.selectedParentWP
    const matchedParentWP = this.WPMainParents.find(
      (item: any) => item.name === this.selectedParentWP?.name
    );

    if (matchedParentWP && matchedParentWP.name === 'CUSTOM') {
      const manual = matchedParentWP.children.find(
        (c: any) => c.name === 'Manual'
      );

      if (manual && manual.children) {
        // Assign children to originalCollection
        this.originalCollection = manual.children.map((child: any) => ({
          ...child,
        }));
        this.collectionL = this.originalCollection.map(
          ({ children, ...item }) => ({
            ...item,
            children: children ? [...children] : undefined,
          })
        );
      } else {
        console.warn(
          `No Work Package found with name matching category: ${this.selectedParentWP}`
        );
        this.originalCollection = []; // Clear if no match
        this.collectionL = [];
      }
    } else {
      if (matchedParentWP && matchedParentWP.children) {
        /**
         * NOTE: this.originalCollection contains the children of select work package
         * NOTE: this.collectionL contains all the children of select work package.
         * WONDER: Do we need to loop, or can we just assign as the objects are not deep copied but referenced
         *
         * IMP:
         * Top-Level Properties:
         *  ...item creates a shallow copy of all the top-level properties.
         *    These are independent in this.collectionL and this.originalCollection.
         *  Children Property:
         *    children ? [...children] creates a shallow copy of the children array.
         *    If children contains objects, the objects themselves are not deeply copied; instead, their references are copied.
         *
         */
        // Assign children to originalCollection
        this.originalCollection = matchedParentWP.children.map(
          (child: any) => ({
            ...child,
          })
        );
        this.collectionL = this.originalCollection.map(
          ({ children, ...item }) => ({
            ...item,
            children: children ? [...children] : undefined,
          })
        );
      } else {
        console.warn(
          `No Work Package found with name matching category: ${this.selectedParentWP}`
        );
        this.originalCollection = []; // Clear if no match
        this.collectionL = [];
      }
    }
    // Detect changes if necessary
    this.cdr.detectChanges();
  }

  /**
   * NOTE: When we select main Parent Work Package from the masurement component, this function is triggered
   */
  updateSelectedWPStandard(selected: WorkPackageInterface) {
    this.selectedParentWP = selected;
    this.collectionR = [];

    if (this.isEdit) {
      this.isUpdated = true;
    }
    this.initializeCollectionL();
  }

  // NOTE: This lifecycle hook is called whenever Angular detects changes to the input-bound properties of a component.
  // ngOnChanges(changes: SimpleChanges) {
  // }

  updateSubCategorySelectedValue(selected: WorkPackageInterface) {
    this.subCategory = selected;
  }

  // Navigate to cost estimation if at least one package is selected
  saveAndNavigateCostEstimation(action: string) {
    const selectedWPSIItems = this.collectionR || [];
    // this.dataService.setData('work_package_standard_id', selectedWPSId);

    this.dataService.setData('work_package_standard_id', 1);
    this.dataService.setData('work_package_items', selectedWPSIItems);

    const dataToSubmit = new FormData();

    const projectId = this.getProjectIdService.getProjectId();
    if (!projectId) {
      this.toast.danger('Project ID not available', 'DANGER', 4000);
      return;
    }

    dataToSubmit.append('project_id', projectId!);
    dataToSubmit.append('is_updated', this.isUpdated ? 'true' : 'false');
    selectedWPSIItems.forEach((item: any) => {
      if (item.children.length > 0) {
        item.children.forEach((child: any) => {
          dataToSubmit.append('work_packages[][id]', child.id);
        });
      } else {
        dataToSubmit.append('work_packages[][id]', item.id);
      }
    });

    if (action === 'next') {
      this.isLoadingNext = true;
    } else {
      this.isLoadingSave = true;
    }

    this.projectsService.saveWorkPackages(dataToSubmit).subscribe({
      next: () => {
        this.toast.success('Section saved successfully', 'SUCCESS', 4000);
        this.progressService.updateStepProgress(PlanStep.WorkPackages, true);

        if (action === 'next') {
          this.isLoadingNext = false;
          this.isUpdated = false;
          this.TabbedScrollerComponent.nextStep();
        }
        if (action === 'save') {
          this.isLoadingSave = false;
          this.isUpdated = false;
        }
      },
      error: (error: any) => {
        if (action === 'next') {
          this.isLoadingNext = false;
        } else {
          this.isLoadingSave = false;
        }

        console.error('Login failed:', error);
        this.toast.danger(error.error.message, 'DANGER', 4000);
      },
    });

    if (this.collectionR.length > 0) {
      // this.router.navigate(['/cost-estimation']);
      this.dataService.setData('work_package_items', this.collectionR);
      this.dataService.setData('package_name', 'NRM');
    } else {
      this.toast.warning(
        'At least one package must be selected',
        'WARNING',
        4000
      ); // Show error toast
    }
  }

  /**
   * NOTE: For Custom
   */
  addmore() {
    const dialogRef = this.dialog.open(DialogboxComponent, {
      panelClass: 'custom-dialog-container'
    });
  
    dialogRef.afterClosed().subscribe(() => {
      this.getAllWPSI();
    });
  }

  // Navigate to the previous page
  navigatetoPreviousPage() {
    this.location.back();
  }

  /**
   * NOTE: when user clicks on parent in left panel, we move it to right
   */
  moveParentToCollectionR(parentId: number | undefined): void {
    const parentIndex = this.collectionL.findIndex(
      (item) => item.id === parentId
    );
    // NOTE: if we have a valid parent, remove it from left panel and move it to right.
    if (parentIndex > -1) {
      const [parent] = this.collectionL.splice(parentIndex, 1);
      this.collectionR.push(parent);
    }
    if (this.isEdit) {
      this.isUpdated = true;
    }
  }

  /**
   * NOTE: when user removes a parent in right panel, we move it to left
   */
  moveParentToCollectionL(parentRId: number | undefined): void {
    // Find the parent in collectionR
    const parentR = this.collectionR.find((p) => p.id === parentRId);
    if (parentR) {
      // Check if the parent already exists in collectionL
      const existingParentL = this.collectionL.find((p) => p.id === parentRId);

      /**
       * NOTE: if we have parent in both panels. i.e. a child was moved to other panel.
       */
      if (existingParentL) {
        // Merge the children into the existing parent in collectionL
        // existingParentL.children!.push(...parent.children);
        console.log('got here manananananaannaan');
        parentR.children!.forEach((child) => {
          this.moveChildToCollectionLUsingParent(parentR, child.id);
        });
      } else {
        const parentIndex = this.collectionR.findIndex(
          (item) => item.id === parentRId
        );
        if (parentIndex > -1) {
          const [parent] = this.collectionR.splice(parentIndex, 1);
          const originalIndex = this.originalCollection.findIndex(
            (oItem) => oItem.id === parentRId
          );
          this.collectionL.splice(originalIndex, 0, parent);
        }
      }

      // Remove the parent and its children from collectionR
      this.collectionR = this.collectionR.filter((p) => p.id !== parentRId);
    }
    if (this.isEdit) {
      this.isUpdated = true;
    }
  }

  /**
   * NOTE: this function receives CollectionR parent and its child id
   *  then gets index in CollectionR specific parent
   *  then removes it from that parent and adds in CollectionL parent
   *  then if parentR does not have anymore children, removes it also
   */
  moveChildToCollectionLUsingParent(
    parentR: WorkPackageInterface,
    childRId: number | undefined
  ): void {
    if (!parentR) {
      return;
    }
    // Remove child from Collection R's parent
    const childIndex =
      parentR.children?.findIndex((child: any) => child.id === childRId) ?? -1;

    // If child does not exist in parentR
    if (childIndex < 0) {
      return;
    }

    const [child] = parentR.children!.splice(childIndex, 1);

    let parentInL = this.collectionL.find((item) => item.id === parentR.id);
    /**
     * NOTE: if all children are moved to right side, we need to
     *  again add that parent in CollectionL with only specified parent
     */
    if (!parentInL) {
      const originalIndex = this.originalCollection.findIndex(
        (oItem) => oItem.id === parentR.id
      );
      parentInL = { ...parentR, children: [] };
      this.collectionL.splice(originalIndex, 0, parentInL);
    }
    parentInL.children!.push(child);

    // Remove parent from Collection R if no children left
    if (parentR.children && parentR.children.length === 0) {
      const parentIndex = this.collectionR.findIndex(
        (item) => item.id === parentR.id
      );
      if (parentIndex > -1) this.collectionR.splice(parentIndex, 1);
    }
  }

  moveChildToCollectionR(
    parentLId: number | undefined,
    childLId: number | undefined
  ): void {
    // Find parent in Collection L
    const parentL = this.collectionL.find((itemL) => itemL.id === parentLId);
    if (parentL) {
      // Remove child from Collection L's parent
      const childLIndex =
        parentL.children?.findIndex((childL: any) => childL.id === childLId) ??
        -1;
      if (childLIndex > -1) {
        const [childL] = parentL.children!.splice(childLIndex, 1);

        /**
         * NOTE: Check if parent exists in Collection R;
         *  if not, add it with the moved child
         *  else, move child into the parent
         */
        let parentInR = this.collectionR.find(
          (itemR) => itemR.id === parentLId
        );
        if (!parentInR) {
          parentInR = { ...parentL, children: [] };
          this.collectionR.push(parentInR);
        }
        parentInR.children!.push(childL);

        // Remove parent from Collection L if no children left
        if (parentL.children && parentL.children.length === 0) {
          const parentLIndex = this.collectionL.findIndex(
            (itemL) => itemL.id === parentLId
          );
          if (parentLIndex > -1) this.collectionL.splice(parentLIndex, 1);
        }
      }
    }
    if (this.isEdit) {
      this.isUpdated = true;
    }
  }

  moveChildToCollectionL(
    parentRId: number | undefined,
    childRId: number | undefined
  ): void {
    // Find parent in Collection R
    const parentR: WorkPackageInterface | undefined = this.collectionR.find(
      (itemR) => itemR.id === parentRId
    );
    this.moveChildToCollectionLUsingParent(parentR!, childRId);
    if (this.isEdit) {
      this.isUpdated = true;
    }
  }

  toggleExpand(parentId: number, collection: 'L' | 'R'): void {
    if (collection === 'L') {
      if (this.expandedLIds.has(parentId)) {
        this.expandedLIds.delete(parentId);
      } else {
        this.expandedLIds.add(parentId);
      }
    } else if (collection === 'R') {
      if (this.expandedRIds.has(parentId)) {
        this.expandedRIds.delete(parentId);
      } else {
        this.expandedRIds.add(parentId);
      }
    }
  }

  isExpanded(parentId: number, collection: 'L' | 'R'): boolean {
    return collection === 'L'
      ? this.expandedLIds.has(parentId)
      : this.expandedRIds.has(parentId);
  }

  handleBack() {
    this.TabbedScrollerComponent.previousStep();
  }
  closeDialog() {
    this.dialogService.closeProjectPlanDialog();
    if (this.collectionR.length > 0) {
      this.saveAndNavigateCostEstimation('save');
    }
  }
}
